/**
 * The theme preference, persisted.
 *
 * **This is the second module allowed to touch `localStorage`, and the split
 * is deliberate.** `src/auth/session-store.ts` holds the session; this holds
 * one UI preference and can never hold anything else.
 * `docs/decisions/0003-ui-internal-persists-one-id-token.md` exists to keep
 * *credentials* out of Web Storage — the rule the lint message states is
 * "tokens stay in memory" — and a theme name is not a credential. Widening
 * the exemption to a named second file, rather than relaxing the rule, is what
 * keeps the list of things this origin persists short and readable. See
 * `docs/decisions/0005-the-console-has-a-dark-theme.md`.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else, the
 * same narrowing `session-store.ts` gets: `sessionStorage`, `indexedDB`,
 * `caches`, `document.cookie`, every `window.`-prefixed spelling and the
 * computed form all still fail the gate here.
 *
 * The stored value is one of three literals and is validated on the way out,
 * so this key cannot be used to smuggle arbitrary content into the origin.
 *
 * Both entry points swallow a storage failure. A browser with storage disabled
 * or a full quota must leave the console working — it simply forgets the
 * choice on the next reload.
 */

import { type ThemePreference, parsePreference } from './theme.ts'

/**
 * The only key this module writes.
 *
 * `index.html` repeats this string in the pre-paint script, because that
 * script runs before any module loads and so cannot import it.
 * `src/theme-prepaint.spec.js` asserts the two stay in step.
 */
export const THEME_KEY = 'ui-internal.theme'

/**
 * Read the held preference.
 *
 * @returns The preference, or null when there is none, the key is unreadable,
 *   or its contents are not one this module writes.
 */
export function readPreference(): ThemePreference | null {
  try {
    return parsePreference(localStorage.getItem(THEME_KEY))
  } catch {
    return null
  }
}

/**
 * Hold a preference across reloads.
 *
 * @param preference - What the reader chose.
 */
export function writePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_KEY, preference)
  } catch {
    // Quota, or storage disabled. The choice applies to this tab and simply
    // does not survive the next reload.
  }
}

/**
 * Forget the held preference, so the next load follows the OS again.
 */
export function clearPreference(): void {
  try {
    localStorage.removeItem(THEME_KEY)
  } catch {
    // Storage disabled: there is nothing held to forget.
  }
}
